import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { PerioMeasurement, PerioSite } from '@/utils/perio';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PerioExamListItem {
  exam_id: string;
  recorded_at: string;
  recorded_by_name: string | null;
  measurement_count: number;
  psr_score: unknown | null;
  worst_pd_mm: number | null;
}

// ── Query keys ─────────────────────────────────────────────────────────────────

const perioExamsListKey = (patientId: string | null) =>
  ['dental', 'perio-exams-list', patientId] as const;

const perioExamKey = (examId: string | null) =>
  ['dental', 'perio-exam', examId] as const;

// ── usePerioExamList ──────────────────────────────────────────────────────────

/**
 * Fetches summary list of all perio exams for a patient via `get_patient_perio_exams`.
 */
export function usePerioExamList(patientId: string | null) {
  return useQuery({
    queryKey: perioExamsListKey(patientId),
    enabled: Boolean(patientId),
    staleTime: 30_000,
    queryFn: async (): Promise<PerioExamListItem[]> => {
      if (!patientId) return [];

      const { data, error } = await supabase.rpc('get_patient_perio_exams', {
        p_patient_id: patientId,
      });

      if (error) throw error;

      return (data ?? []).map((row: unknown) => {
        const r = row as Record<string, unknown>;
        return {
          exam_id: r.exam_id as string,
          recorded_at: r.recorded_at as string,
          recorded_by_name: (r.recorded_by_name as string | null) ?? null,
          measurement_count: Number(r.measurement_count ?? 0),
          psr_score: r.psr_score ?? null,
          worst_pd_mm: r.worst_pd_mm != null ? Number(r.worst_pd_mm) : null,
        };
      });
    },
  });
}

// ── usePerioExam ──────────────────────────────────────────────────────────────

const SITE_ORDER: Record<PerioSite, number> = {
  MB: 0, B: 1, DB: 2, ML: 3, L: 4, DL: 5,
};

/**
 * Fetches all perio_measurements for a single exam, ordered by FDI then site.
 */
export function usePerioExam(examId: string | null) {
  return useQuery({
    queryKey: perioExamKey(examId),
    enabled: Boolean(examId),
    staleTime: 30_000,
    queryFn: async (): Promise<PerioMeasurement[]> => {
      if (!examId) return [];

      const { data, error } = await supabase
        .from('perio_measurements')
        .select('*')
        .eq('exam_id', examId);

      if (error) throw error;

      return (data ?? [])
        .map((row) => ({
          tooth_fdi: row.tooth_fdi,
          site: row.site as PerioSite,
          pocket_depth_mm: row.pocket_depth_mm,
          recession_mm: row.recession_mm,
          cal_mm: row.cal_mm,
          bleeding_on_probing: row.bleeding_on_probing,
          suppuration: row.suppuration,
          mobility: row.mobility,
          furcation: row.furcation,
        }))
        .sort((a, b) => {
          const fdiCmp = a.tooth_fdi.localeCompare(b.tooth_fdi);
          if (fdiCmp !== 0) return fdiCmp;
          return (SITE_ORDER[a.site] ?? 0) - (SITE_ORDER[b.site] ?? 0);
        });
    },
  });
}

// ── useRecordPerioExam ────────────────────────────────────────────────────────

interface RecordPerioExamArgs {
  patientId: string;
  examId?: string | null;
  measurements: Omit<PerioMeasurement, 'cal_mm'>[];
}

/**
 * Batch-upserts all measurements for one exam via `record_perio_exam` RPC.
 * Returns the exam_id used.
 */
export function useRecordPerioExam() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patientId,
      examId,
      measurements,
    }: RecordPerioExamArgs): Promise<string> => {
      const { data, error } = await supabase.rpc('record_perio_exam', {
        p_patient_id: patientId,
        ...(examId != null && { p_exam_id: examId }),
        p_measurements: measurements as unknown as never,
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: (returnedExamId, { patientId }) => {
      void qc.invalidateQueries({ queryKey: perioExamsListKey(patientId) });
      void qc.invalidateQueries({ queryKey: perioExamKey(returnedExamId) });
    },
  });
}

// ── useUpdatePerioMeasurement ─────────────────────────────────────────────────

interface UpdateMeasurementArgs {
  id: string;
  patientId: string;
  examId: string;
  pocket_depth_mm?: number | null;
  recession_mm?: number | null;
  bleeding_on_probing?: boolean;
  suppuration?: boolean;
  mobility?: number | null;
  furcation?: number | null;
}

export function useUpdatePerioMeasurement() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      pocket_depth_mm,
      recession_mm,
      bleeding_on_probing,
      suppuration,
      mobility,
      furcation,
    }: UpdateMeasurementArgs): Promise<void> => {
      const { error } = await supabase.rpc('update_perio_measurement', {
        p_id: id,
        p_pocket_depth_mm: pocket_depth_mm ?? undefined,
        p_recession_mm: recession_mm ?? undefined,
        p_bleeding: bleeding_on_probing ?? undefined,
        p_suppuration: suppuration ?? undefined,
        p_mobility: mobility ?? undefined,
        p_furcation: furcation ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: (_data, { patientId, examId }) => {
      void qc.invalidateQueries({ queryKey: perioExamsListKey(patientId) });
      void qc.invalidateQueries({ queryKey: perioExamKey(examId) });
    },
  });
}

// ── useDeletePerioExam ────────────────────────────────────────────────────────

export function useDeletePerioExam() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patientId,
      examId,
    }: {
      patientId: string;
      examId: string;
    }): Promise<void> => {
      const { error } = await supabase.rpc('delete_perio_exam', {
        p_patient_id: patientId,
        p_exam_id: examId,
      });
      if (error) throw error;
    },
    onSuccess: (_data, { patientId, examId }) => {
      void qc.invalidateQueries({ queryKey: perioExamsListKey(patientId) });
      void qc.invalidateQueries({ queryKey: perioExamKey(examId) });
    },
  });
}
